/* Geometry for the path graph: world bounds, the curve between two chapters,
 * the minimap projection and the viewport arithmetic. All pure — the DOM only
 * supplies the container size and receives a transform.
 */
import { NODES } from '../data/nodes';
import type { NodeDef } from '../types';

export const WORLD = (() => {
  let w = 0;
  let h = 0;
  for (const n of NODES) {
    w = Math.max(w, n.x + n.w);
    h = Math.max(h, n.y + n.h);
  }
  return { w: w + 40, h: h + 40 };
})();

export const MINI = { w: 186, h: 116, pad: 9 };

export interface View {
  x: number;
  y: number;
  k: number;
}

export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 1.6;
export const NARROW_MAX = 1040;

/* The artifact drew three shapes of connector: down into an optional stage,
   across to the next chapter, and down when neither applies. */
export function edgePath(a: NodeDef, b: NodeDef): string {
  if (b.extra && b.y > a.y && b.x >= a.x - 40 && b.x <= a.x + a.w) {
    const x1 = a.x + a.w / 2;
    const y1 = a.y + a.h;
    const x2 = b.x + b.w / 2;
    const y2 = b.y;
    const dy = Math.max(26, (y2 - y1) * 0.5);
    return `M${x1} ${y1} C${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
  }
  if (b.x > a.x) {
    const x1 = a.x + a.w;
    const y1 = a.y + a.h / 2;
    const x2 = b.x;
    const y2 = b.y + b.h / 2;
    const dx = Math.max(34, (x2 - x1) * 0.5);
    return `M${x1} ${y1} C${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }
  const x1 = a.x + a.w / 2;
  const y1 = a.y + a.h;
  const x2 = b.x + b.w / 2;
  const y2 = b.y;
  const dy2 = Math.max(30, (y2 - y1) * 0.55 + 10);
  return `M${x1} ${y1} C${x1} ${y1 + dy2}, ${x2} ${y2 - dy2}, ${x2} ${y2}`;
}

export function fitView(w: number, h: number, narrow: boolean): View {
  const pad = narrow ? 16 : 56;
  const kmin = narrow ? 0.16 : 0.3;
  let k = Math.min((w - pad * 2) / WORLD.w, (h - pad * 2) / WORLD.h);
  k = Math.max(kmin, Math.min(1.1, k));
  return { k, x: (w - WORLD.w * k) / 2, y: (h - WORLD.h * k) / 2 };
}

export function zoomAt(view: View, factor: number, cx: number, cy: number): View {
  const k = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, view.k * factor));
  return {
    k,
    x: cx - (cx - view.x) * (k / view.k),
    y: cy - (cy - view.y) * (k / view.k),
  };
}

/* Centre a chapter in the canvas. The artifact reserved the drawer's width
   here, but the drawer only exists in the map view, where the canvas is hidden
   — so the free width is the whole canvas. */
export function focusView(n: NodeDef, view: View, w: number, h: number): View {
  const k = Math.min(1, Math.max(view.k, 0.86));
  return { k, x: w / 2 - (n.x + n.w / 2) * k, y: h / 2 - (n.y + n.h / 2) * k };
}

export function miniScale(): number {
  return Math.min((MINI.w - MINI.pad * 2) / WORLD.w, (MINI.h - MINI.pad * 2) / WORLD.h);
}

/* Level of detail: zoomed out the graph is a map of chapters, zoomed in it
   shows the steps inside each one. */
export function levelOfDetail(k: number): 'far' | 'near' {
  return k < 0.62 ? 'far' : 'near';
}
